import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Comparison() {
  const consultationLink = "https://kronologic.me/1-click/zWxqVT2ASOytpMCtSReRDWxbBRVHiUbHjzqAm5x3Arg";

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-between items-center py-8">
            <a href="https://icpedge.com" className="w-48">
              <img 
                src="/logo.png" 
                alt="ICP EDGE Logo" 
                className="w-full h-auto brightness-0 invert"
              />
            </a>
            <div className="flex items-center gap-6">
              <Link 
                to="/tools/decision-tree" 
                className="text-white hover:text-blue-100 transition-colors font-semibold"
              >
                Find Your Edge
              </Link>
              <Link 
                to="/tools/conversion-unlocker" 
                className="text-white hover:text-blue-100 transition-colors font-semibold"
              >
                Fix Conversion Blockers
              </Link>
              <a 
                href={consultationLink}
                className="px-6 py-3 rounded-lg font-semibold bg-white text-blue-600 hover:bg-blue-50 transition-colors"
              >
                Partner With Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-6xl py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold mb-8">
              Enhancing Customer Insights:<br /> How ICP EDGE Complements Qualtrics Data
            </h1>
            
            <p className="text-xl text-gray-600 mb-12">
              Together, we can bridge data and psychology to unlock deeper customer understanding.
            </p>

            <div className="bg-blue-50 p-8 rounded-xl mb-12">
              <p className="text-lg mb-6">
                As experienced CX professionals, you've mastered the art of gathering and analyzing customer feedback. 
                Qualtrics provides powerful capabilities for collecting and organizing valuable data.
              </p>

              <h2 className="text-2xl font-bold mb-4">Where Collaborative Insights Create New Possibilities</h2>
              <p className="mb-6">
                We see a shared opportunity: combining robust data platforms with psychological insights to create even more comprehensive understanding.
              </p>

              <ul className="space-y-4">
                {[
                  'Enhance existing journey maps with deeper motivational context',
                  'Collaborate on uncovering both conscious and unconscious decision drivers',
                  'Complement quantitative excellence with qualitative psychological dimensions'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
                <h3 className="text-xl font-bold mb-6">Qualtrics Strengths</h3>
                <ul className="space-y-4">
                  {[
                    'Comprehensive multi-channel data collection',
                    'Unified customer feedback management',
                    'Pattern recognition across touchpoints',
                    'Powerful operational dashboards'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl border-2 border-blue-200">
                <h3 className="text-xl font-bold mb-6">ICP EDGE ConsumerDNA™ Complements By Adding</h3>
                <ul className="space-y-4">
                  {[
                    'Psychological dimension to existing analytics',
                    'Behavioral science perspectives on customer journeys',
                    'Cognitive bias identification to enhance understanding',
                    'Emotional context for quantitative findings'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6">A Partnership Approach to Enhanced Insights</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <h4 className="font-bold mb-2">Complement Your Expertise</h4>
                    <p>ConsumerDNA™ works alongside your existing data, respecting your methodologies while offering additional perspective.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <h4 className="font-bold mb-2">Collaborative Intelligence</h4>
                    <p>Combine your data expertise with psychological insights to develop more comprehensive strategies.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <h4 className="font-bold mb-2">Shared Success</h4>
                    <p>Measure outcomes together to refine approaches and create even more effective solutions.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-4">The Power of Combined Perspectives</h2>
              <p className="text-lg">
                When data analytics and psychological insights work together, we can develop more nuanced understanding 
                of customer behavior and create more effective experience strategies.
              </p>
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-bold mb-4">Let's Explore Possibilities Together</h2>
              <p className="text-lg mb-8">
                Every CX team has unique strengths and approaches. We'd welcome a conversation about how ConsumerDNA™ 
                might complement your existing Qualtrics capabilities to bring new dimensions to your already 
                sophisticated customer understanding.
              </p>
              <a 
                href={consultationLink}
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Partner With Us
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}